import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isFunctionPillNode, FunctionPillNode } from "components/CC/UI/TagInput/FunctionPillNode";
import { type DropIndicatorState, type GhostState } from "components/CC/UI/TagInput/tagInputTypes";
import { getTagPillColor } from "components/CC/UI/TagInput/tagInputUtils";
import { $createTagNode, $isTagNode, TagNode } from "components/CC/UI/TagInput/TagNode";
import {
    $getNodeByKey,
    $getNearestNodeFromDOMNode,
    $getRoot,
    $isElementNode,
    $isTextNode,
    type LexicalNode,
} from "lexical";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";

// A drop point landing on either kind of atomic inline pill — a plain tag or a function-call
// paren marker — resolves the same way: before/after based on which half of its box the pointer
// is over. Without this, dropping near/on a FunctionPillNode fell through every branch below and
// silently appended at the very end of the field instead of where the user actually dropped it.
const $isDragAnchorNode = (node: LexicalNode | null): node is TagNode | FunctionPillNode =>
    $isTagNode(node) || $isFunctionPillNode(node);

const insertTagAtPoint = (
    editor: ReturnType<typeof useLexicalComposerContext>[0],
    nodeKey: string,
    x: number,
    y: number
): void => {
    editor.update(() => {
        const dragNode = $getNodeByKey(nodeKey);
        if (!$isTagNode(dragNode)) return;

        const cloned = $createTagNode(
            dragNode.getInner(),
            dragNode.getParamType(),
            dragNode.isValid(),
            dragNode.getDisplayLabel(),
            dragNode.getNodeType()
        );
        dragNode.remove();

        let inserted = false;
        const domRange = document.caretRangeFromPoint?.(x, y);

        if (domRange) {
            try {
                const lexNode = $getNearestNodeFromDOMNode(domRange.startContainer);
                const offset = domRange.startOffset;

                if ($isTextNode(lexNode)) {
                    const size = lexNode.getTextContentSize();
                    if (offset === 0) {
                        lexNode.insertBefore(cloned);
                    } else if (offset >= size) {
                        lexNode.insertAfter(cloned);
                    } else {
                        const [, after] = lexNode.splitText(offset);
                        (after ?? lexNode).insertBefore(cloned);
                    }
                    inserted = true;
                } else if ($isDragAnchorNode(lexNode)) {
                    const targetElement = editor.getElementByKey(lexNode.getKey());
                    if (targetElement) {
                        const rect = targetElement.getBoundingClientRect();
                        const shouldInsertBefore = x < rect.left + rect.width / 2;
                        if (shouldInsertBefore) {
                            lexNode.insertBefore(cloned);
                        } else {
                            lexNode.insertAfter(cloned);
                        }
                    } else {
                        lexNode.insertAfter(cloned);
                    }
                    inserted = true;
                } else if ($isElementNode(lexNode)) {
                    lexNode.append(cloned);
                    inserted = true;
                }
            } catch {
                // fall through to append
            }
        }

        if (!inserted) {
            const root = $getRoot();
            const lastPara = root.getLastChild();
            if ($isElementNode(lastPara)) {
                lastPara.append(cloned);
            }
        }
    });
};

export const TagDragDropPlugin = (): React.JSX.Element | null => {
    const [editor] = useLexicalComposerContext();
    const [ghost, setGhost] = useState<GhostState | null>(null);
    const [dropIndicator, setDropIndicator] = useState<DropIndicatorState | null>(null);

    useEffect(() => {
        const handleTagDragStart = (e: Event) => {
            const { nodeKey, inner, paramType } = (
                e as CustomEvent<{ nodeKey: string; inner: string; paramType: TagNode["__paramType"] }>
            ).detail;

            const hostEl = editor.getElementByKey(nodeKey) as HTMLElement | null;
            if (hostEl) hostEl.style.opacity = "0";
            document.body.style.userSelect = "none";

            const cleanup = () => {
                if (hostEl) hostEl.style.opacity = "";
                document.body.style.userSelect = "";
                setGhost(null);
                setDropIndicator(null);
                window.removeEventListener("pointermove", handleMove);
                window.removeEventListener("pointerup", handleUp);
            };

            const handleMove = (ev: PointerEvent) => {
                setGhost({ inner, paramType, x: ev.clientX, y: ev.clientY });
                const rootEl = editor.getRootElement();
                const range = document.caretRangeFromPoint?.(ev.clientX, ev.clientY);
                if (range) {
                    const sel = window.getSelection();
                    sel?.removeAllRanges();
                    sel?.addRange(range);

                    let tagNodeKey: string | null = null;
                    editor.read(() => {
                        const lexNode = $getNearestNodeFromDOMNode(range.startContainer);
                        if ($isDragAnchorNode(lexNode)) {
                            tagNodeKey = lexNode.getKey();
                        }
                    });
                    if (tagNodeKey !== null) {
                        const targetElement = editor.getElementByKey(tagNodeKey);
                        if (targetElement) {
                            const rect = targetElement.getBoundingClientRect();
                            const before = ev.clientX < rect.left + rect.width / 2;
                            setDropIndicator({
                                x: before ? rect.left : rect.right,
                                y: rect.top,
                                height: Math.max(rect.height, 16),
                            });
                            return;
                        }
                    }

                    const rect = range.getBoundingClientRect();
                    if (rect && rect.height > 0) {
                        setDropIndicator({
                            x: rect.left,
                            y: rect.top,
                            height: Math.max(rect.height, 16),
                        });
                    } else {
                        if (rootEl) {
                            const firstTagEl = rootEl.querySelector(
                                'span[contenteditable="false"]'
                            ) as HTMLElement | null;
                            if (firstTagEl) {
                                const firstRect = firstTagEl.getBoundingClientRect();
                                if (ev.clientX <= firstRect.left + 2) {
                                    setDropIndicator({
                                        x: firstRect.left,
                                        y: firstRect.top,
                                        height: Math.max(firstRect.height, 16),
                                    });
                                    return;
                                }
                            }
                        }
                        setDropIndicator(null);
                    }
                } else {
                    if (rootEl) {
                        const firstTagEl = rootEl.querySelector('span[contenteditable="false"]') as HTMLElement | null;
                        if (firstTagEl) {
                            const firstRect = firstTagEl.getBoundingClientRect();
                            if (ev.clientX <= firstRect.left + 2) {
                                setDropIndicator({
                                    x: firstRect.left,
                                    y: firstRect.top,
                                    height: Math.max(firstRect.height, 16),
                                });
                                return;
                            }
                        }
                    }
                    setDropIndicator(null);
                }
            };

            const handleUp = (ev: PointerEvent) => {
                cleanup();
                const rootEl = editor.getRootElement();
                const elAtPoint = document.elementFromPoint(ev.clientX, ev.clientY);
                if (rootEl && elAtPoint && rootEl.contains(elAtPoint)) {
                    insertTagAtPoint(editor, nodeKey, ev.clientX, ev.clientY);
                }
            };

            // Set up listeners synchronously — avoids race condition where pointerup
            // fires before React processes the ghost state change.
            window.addEventListener("pointermove", handleMove);
            window.addEventListener("pointerup", handleUp);
        };

        return editor.registerRootListener((rootElement, prevRootElement) => {
            prevRootElement?.removeEventListener("tag-drag-start", handleTagDragStart);
            rootElement?.addEventListener("tag-drag-start", handleTagDragStart);
        });
    }, [editor]);

    if (!ghost) return null;

    const displayLabel = ghost.inner;

    return ReactDOM.createPortal(
        <>
            <span
                style={{
                    position: "fixed",
                    left: ghost.x + 10,
                    top: ghost.y - 12,
                    backgroundColor: getTagPillColor(ghost.paramType),
                    pointerEvents: "none",
                    zIndex: 9999,
                    opacity: 0.85,
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-white text-xs shadow-lg"
            >
                {displayLabel}
            </span>
            {dropIndicator && (
                <span
                    aria-hidden
                    style={{
                        position: "fixed",
                        left: dropIndicator.x - 1,
                        top: dropIndicator.y,
                        width: 2,
                        height: dropIndicator.height,
                        backgroundColor: "#1677ff",
                        borderRadius: 2,
                        pointerEvents: "none",
                        zIndex: 10000,
                    }}
                />
            )}
        </>,
        document.body
    );
};
